import React from 'react';
import * as Styled from './style';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import SortButton from './SortButton';
import { ScrollArea } from '@mantine/core';
import { idCreator } from '../../pages/Project/utils/idCreator';

interface TableProps {
  data: any[];
  columns: any[];
  isLoading?: boolean;
  onRowClick?: (item: any, isLast: boolean) => void;
  hasIndex?: boolean;
  trClassName?: string;
  className?: string;
  height?: number | string;
  trProps?: any;
  idPrefix?: string;
}

export default function Table({
  data,
  columns,
  isLoading = false,
  onRowClick = () => {},
  hasIndex = true,
  trClassName = '',
  className = '',
  height,
  trProps = {},
  idPrefix = 'table'
}: TableProps) {
  const [sortConfig, setSortConfig] = React.useState<{ key: string | null; direction: string }>({
    key: null,
    direction: 'asc' // or 'desc'
  });

  const requestSort = (_key, sortType) => {
    setSortConfig({ key: _key, direction: sortType });
  };

  const handleRowClick = (item, index) => () => {
    onRowClick(item, index === data?.length - 1);
  };

  const sortedData = React.useMemo(() => {
    if (!data) return [];

    let sortableData = [...data];
    const { key, direction } = sortConfig;

    if (key) {
      sortableData.sort((a, b) => {
        // Handle if the data is numeric or string, or do a custom comparison
        // Below is a simple string/number comparison
        if (a[key] < b[key]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableData;
  }, [data, sortConfig]);

  return (
    // The "$" prefix in $hasIndex marks it as a transient prop for styled-components,
    // so it won't be passed to the underlying DOM element and avoids React warnings.

    <ScrollArea
      id={`${idPrefix}-scroll-area`}
      styles={{
        scrollbar: {
          zIndex: 10
        }
      }}
      pos="relative"
      scrollbarSize={14}
      scrollHideDelay={500}
      h={height}
    >
      {/* @ts-ignore */}
      <Styled.TableContainer
        id={`${idPrefix}-container`}
        $hasIndex={hasIndex}
        className={className}
      >
        <thead id={`${idPrefix}-thead`}>
          <tr id={`${idPrefix}-header-row`}>
            {hasIndex && (
              <th
                id={`${idPrefix}-header-index`}
                style={{
                  width: 27
                }}
              >
                №
              </th>
            )}

            {columns?.map(item => {
              const isSortable = !!item.sort;
              const isCurrentlySorted = sortConfig.key === item.key;

              return (
                <th
                  id={`${idPrefix}-th-${idCreator(item.title || item.key)}`}
                  key={`col-th-${item.key}`}
                  style={item.thStyle}
                >
                  <div
                    id={`${idPrefix}-th-wrapper-${idCreator(item.title || item.key)}`}
                    className="th-wrapper"
                  >
                    {item?.renderHeadCell ? item.renderHeadCell() : item.title}{' '}
                    {isSortable && (
                      <SortButton
                        idPrefix={`${idPrefix}-sort-${idCreator(item.key)}`}
                        sortKey={isCurrentlySorted ? sortConfig.direction : null}
                        onClick={sortType => requestSort(item.key, sortType)}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody id={`${idPrefix}-tbody`}>
          {isLoading &&
            Array.from(Array(12).keys()).map(item => (
              <tr id={`${idPrefix}-loading-row-${item}`} key={item}>
                {hasIndex && (
                  <td
                    id={`${idPrefix}-loading-index-cell-${item}`}
                    style={{
                      verticalAlign: 'top'
                    }}
                  >
                    {item + 1}
                  </td>
                )}
                {columns?.map(value => (
                  <td
                    id={`${idPrefix}-loading-cell-${item}-${idCreator(value.title || value.key)}`}
                    key={value.key + item}
                  >
                    <Skeleton height={25} />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading &&
            sortedData?.map((item, index) => {
              const _trProps = typeof trProps === 'function' ? trProps(item) : trProps;
              return (
                <tr
                  id={`${idPrefix}-row-${item.id || index}`}
                  key={item.id || index}
                  onClick={handleRowClick(item, index)}
                  className={trClassName}
                  {..._trProps}
                >
                  {hasIndex && (
                    <td
                      id={`${idPrefix}-index-cell-${item.id || index}`}
                      style={{
                        verticalAlign: 'top',
                        background: '#fff'
                      }}
                    >
                      <div>{index + 1}</div>
                    </td>
                  )}
                  {columns?.map(value => {
                    const tdProps =
                      typeof value?.tdProps === 'function' ? value?.tdProps(item) : value?.tdProps;
                    return (
                      <td
                        id={`${idPrefix}-cell-${item.id || index}-${idCreator(value.title || value.key)}`}
                        key={`${item.id}-cell-value-${value.key}`}
                        {...tdProps}
                        onClick={
                          value?.tdProps?.onClick ? () => value.tdProps.onClick(item) : () => {}
                        }
                      >
                        {value?.render ? value?.render(item) : item[value.key]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </Styled.TableContainer>
    </ScrollArea>
  );
}
